import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { backAccessibilityLabel } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'

import { BackButtonV2Type as BackButtonType, BackButtonV2 } from '../../atoms/backButton/BackButtonV2'
import { BackButtonSmall } from '../../atoms/backButtonSmall/BackButtonSmall'
import { Image } from '../../atoms/image/Image'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { XButton } from '../../atoms/xButton/XButton'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { ContentContainer } from '../../templates/content/ContentContainer'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { getAssetSource, tID } from '../../utils/utils'

export interface ContactCareNavigatorBaseProps {
  header: ReactNode
  children: ReactNode
  backgroundColor?: string
  onExit?: () => void
  onBack?: () => void
  useSidePanelStyle?: boolean
  hideXButton?: boolean
  headerImage?: JSX.Element
  customContentContainerStyle?: CSSObject
}

const ImageAndContents = styled.View(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.breakpoints.isMinWidthLaptop ? 0 : theme.spacing['48px'],
  marginTop: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
}))

const Illustration = styled(Image)<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMinWidthLaptop ? '310px' : '211px',
  height: theme.breakpoints.isMinWidthLaptop ? '153px' : '107px',
}))

const NavBar = styled.View<{ theme: ThemeType; useSidePanelStyle?: boolean; onFlexEnd?: boolean }>(
  ({
    theme: {
      breakpoints: { isMobileSized },
    },
    useSidePanelStyle,
    onFlexEnd,
  }) => ({
    marginTop: isMobileSized ? (useSidePanelStyle ? 0 : '40px') : '12px',
    flexDirection: 'row',
    justifyContent: onFlexEnd ? 'flex-end' : 'space-between',
    marginRight: '-8px',
    alignItems: 'center',
  }),
)

const IllustrationContainer = styled.View<{ theme: ThemeType; useSidePanelStyle?: boolean }>(
  ({ theme: { spacing }, useSidePanelStyle }) => ({
    marginBottom: useSidePanelStyle ? spacing['24px'] : spacing['16px'],
  }),
)

export const ContactCareNavigatorBase: FunctionComponent<ContactCareNavigatorBaseProps> = ({
  header,
  children,
  backgroundColor,
  onExit,
  onBack,
  useSidePanelStyle = false,
  hideXButton = false,
  headerImage,
  customContentContainerStyle,
}) => {
  const {
    colors,
    assets: { careNavigatorEntryPoint },
  } = useTheme() as ThemeType
  const { isMobileSized } = useMediaQuerySize()
  const { formatMessage } = useIntl()
  const careNavImageUrl = useGetResponsiveAssets(careNavigatorEntryPoint?.assetNames ?? { defaultAsset: '' })

  const shouldShowSmallerVersion = isMobileSized || useSidePanelStyle
  const defaultHeaderImage = (
    <IllustrationContainer useSidePanelStyle={useSidePanelStyle}>
      <Illustration
        source={getAssetSource(careNavImageUrl)}
        contentFit='contain'
        accessibilityIgnoresInvertColors
        testID={tID('CareNavigatorGeneralEntryPointImage')}
      />
    </IllustrationContainer>
  )

  const Contents = styled.View(() => ({
    width: shouldShowSmallerVersion ? '100%' : '624px',
  }))

  const BackButtonContainer = styled.View(
    ({
      theme: {
        breakpoints: { isMobileSized },
        spacing,
      },
    }) => ({
      marginTop: isMobileSized ? (useSidePanelStyle ? spacing['32px'] : spacing['64px']) : spacing['32px'],
    }),
  )

  const StyledContentContainer = styled(ContentContainer)<{ theme: ThemeType }>(
    ({ theme: { breakpoints, spacing } }) => ({
      backgroundColor: backgroundColor || colors.backgroundPrimary,
      paddingHorizontal: useSidePanelStyle ? 32 : breakpoints.isMobileSized ? 16 : 64,
      ...(!breakpoints.isMobileSized && { maxHeight: '100vh', overflowY: 'auto', paddingTop: spacing['12px'] }),
      ...(customContentContainerStyle && { ...customContentContainerStyle }),
    }),
  )

  return (
    <StyledContentContainer>
      {hideXButton && onBack && (
        <BackButtonContainer>
          <BackButtonV2
            testID={tID('ContactCareNavPage-backButton')}
            onPress={onBack}
            backButtonType={BackButtonType.OATMEAL}
          />
        </BackButtonContainer>
      )}
      {!hideXButton && (
        <NavBar useSidePanelStyle={useSidePanelStyle} onFlexEnd={hideXButton || !onBack}>
          {onBack && (
            <BackButtonSmall
              testID={tID('ContactCareNavPage-backButton')}
              onPress={onBack}
              accessibilityLabel={formatMessage(backAccessibilityLabel)}
            />
          )}
          {onExit && (
            <XButton
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close',
                description: 'Close button to exit',
              })}
              testID={tID('ContactCareNavPage-xButton')}
              onPress={onExit}
              showExitText={false}
              size={24}
            />
          )}
        </NavBar>
      )}
      <ImageAndContents testID={tID('ContactCareNavigatorBase')}>
        {headerImage || defaultHeaderImage}
        <Contents>
          <Title
            size={shouldShowSmallerVersion ? TitleSize.MEDIUM : TitleSize.LARGE}
            text={header}
            testID={tID('ContactCareNavigatorBase-headertext')}
            level='1'
            color={colors.textPrimary}
            textAlign='center'
          />
          {children}
        </Contents>
      </ImageAndContents>
    </StyledContentContainer>
  )
}
